// ===================================================================
// MAINSTREET — Reports
// CSV export for organization roster, damage reports, status check
// results, resource offers, facilities, contacts
// Ported from mainstreet-embedded.html renderReports() + exports
// ===================================================================

import { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Title, Text, Paper, SimpleGrid, Group, Button,
  TextInput, Loader, Center, Select,
} from '../../components/gab/GabComponents';
import {
  IconFileTypeCsv, IconBuilding,
  IconAlertTriangle, IconPackage, IconClipboardCheck,
  IconBuildingWarehouse, IconAddressBook,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import dayjs from 'dayjs';
import Papa from 'papaparse';
import { useMainStreetStore } from '../../store/mainstreetStore';
import { useMainStreetOpsStore } from '../../store/mainstreetOpsStore';
import { useAuthStore } from '../../store/authStore';
import { SECTOR_TYPES, OP_STATUS, OFFER_TYPES } from './constants';
import { primary, semantic } from '../../lib/colors';

// ── Helpers ─────────────────────────────────────────────────────

function sectorLabel(id: string): string {
  const s = SECTOR_TYPES.find(st => st.id === id);
  return s ? s.label : (id || 'Other').replace(/_/g, ' ');
}

function opLabel(id: string): string {
  const o = OP_STATUS.find(s => s.id === id);
  return o ? o.label : (id || 'Unknown').replace(/_/g, ' ');
}

function offerLabel(id: string): string {
  const o = OFFER_TYPES.find(t => t.id === id);
  return o ? o.label : (id || 'Other').replace(/_/g, ' ');
}

function downloadCSV(data: Record<string, unknown>[], filename: string) {
  const csv = Papa.unparse(data);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  notifications.show({ title: 'Export Complete', message: `${filename} downloaded`, color: 'green' });
}

function todayISO() {
  return dayjs().format('YYYY-MM-DD');
}

// ── Report Type Definition ──────────────────────────────────────

interface ReportType {
  id: string;
  label: string;
  icon: React.ReactNode;
  description: string;
  color: string;
}

const REPORT_TYPES: ReportType[] = [
  { id: 'organizations', label: 'Organization Roster', icon: <IconBuilding size={16} />, description: 'All registered organizations with status, sector, contact info', color: '#0891b2' },
  { id: 'damage', label: 'Damage Report Summary', icon: <IconAlertTriangle size={16} />, description: 'Damage reports with severity, type, and organization details', color: semantic.error },
  { id: 'status', label: 'Status Check Results', icon: <IconClipboardCheck size={16} />, description: 'Status check responses from organizations', color: primary[700] },
  { id: 'offers', label: 'Resource Offers', icon: <IconPackage size={16} />, description: 'Available resource offers from organizations', color: '#07963F' },
  { id: 'facilities', label: 'Facilities', icon: <IconBuildingWarehouse size={16} />, description: 'All facility records with building details, generator, and hazmat info', color: '#0285C4' },
  { id: 'contacts', label: 'Contacts', icon: <IconAddressBook size={16} />, description: 'Contact directory for all organizations', color: '#A627FF' },
];

export default function Reports() {
  const { organizations, facilities, contacts, loadOrganizations, loadFacilities, loadContacts } = useMainStreetStore();
  const {
    statusChecks, statusResponses, damageReports, resourceOffers,
    loadStatusChecks, loadStatusResponses, loadDamageReports, loadResourceOffers,
  } = useMainStreetOpsStore();
  const { incidents, activeIncident } = useAuthStore();

  const [initialLoad, setInitialLoad] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedIncidentId, setSelectedIncidentId] = useState<string | null>(
    activeIncident ? String(activeIncident.id) : null,
  );

  const fetchData = useCallback(async () => {
    await Promise.all([
      loadOrganizations(), loadFacilities(), loadContacts(),
      loadStatusChecks(), loadStatusResponses(),
      loadDamageReports(), loadResourceOffers(),
    ]);
  }, [loadOrganizations, loadFacilities, loadContacts, loadStatusChecks, loadStatusResponses, loadDamageReports, loadResourceOffers]);

  useEffect(() => {
    fetchData().finally(() => setInitialLoad(false));
  }, [fetchData]);

  // Sync default when activeIncident changes
  useEffect(() => {
    if (activeIncident && !selectedIncidentId) {
      setSelectedIncidentId(String(activeIncident.id));
    }
  }, [activeIncident, selectedIncidentId]);

  const incidentOptions = useMemo(() =>
    incidents.map(i => ({ value: String(i.id), label: i.name })),
    [incidents],
  );

  // Incident filter
  const incId = selectedIncidentId || null;
  const selectedIncidentName = useMemo(() => {
    if (!selectedIncidentId) return '';
    const inc = incidents.find(i => String(i.id) === selectedIncidentId);
    return inc ? inc.name.replace(/[^a-zA-Z0-9-_ ]/g, '').replace(/\s+/g, '-') : '';
  }, [selectedIncidentId, incidents]);
  const incDamage = useMemo(() => incId ? damageReports.filter(d => String(d.incidentId) === incId) : damageReports, [damageReports, incId]);
  const incOffers = useMemo(() => incId ? resourceOffers.filter(r => String(r.incidentId) === incId) : resourceOffers, [resourceOffers, incId]);
  const incChecks = useMemo(() => incId ? statusChecks.filter(c => String(c.incidentId) === incId) : statusChecks, [statusChecks, incId]);

  // Date filter helper
  const inDateRange = useCallback((dateStr: string) => {
    if (!dateStr) return true;
    if (!dateFrom && !dateTo) return true;
    const d = dayjs(dateStr);
    if (dateFrom && d.isBefore(dayjs(dateFrom), 'day')) return false;
    if (dateTo && d.isAfter(dayjs(dateTo), 'day')) return false;
    return true;
  }, [dateFrom, dateTo]);

  // ── Export Functions ──────────────────────────────────────────

  const exportOrganizations = () => {
    const rows = organizations.map(o => {
      const dmgCount = damageReports.filter(d => String(d.orgId) === String(o.id)).length;
      const offerCount = resourceOffers.filter(r => String(r.orgId) === String(o.id)).length;
      return {
        Name: o.name,
        Sector: sectorLabel(o.sectorType),
        Address: o.address,
        City: o.city,
        State: o.state,
        ZIP: o.zip,
        Phone: o.phone,
        Email: o.email,
        PrimaryContact: o.primaryContact,
        OperationalStatus: opLabel(o.opStatus),
        Insurance: o.insurance,
        ContinuityPlan: o.continuity,
        LastVerified: o.lastVerified,
        DamageReports: dmgCount,
        ResourceOffers: offerCount,
        Notes: o.notes,
      };
    });
    downloadCSV(rows, `organizations_${todayISO()}.csv`);
  };

  const exportDamage = () => {
    const filtered = incDamage.filter(d => inDateRange(d.reportedAt));
    const rows = filtered.map(d => ({
      OrgName: d.orgName,
      Severity: d.severity,
      DamageTypes: d.damageTypes,
      OpStatus: opLabel(d.opStatus),
      Description: d.description,
      ReportedBy: d.reportedBy,
      ReportedAt: d.reportedAt,
    }));
    downloadCSV(rows, `damage-reports${selectedIncidentName ? `-${selectedIncidentName}` : ''}-${todayISO()}.csv`);
  };

  const exportStatus = () => {
    const rows: Record<string, unknown>[] = [];
    incChecks.forEach(c => {
      const responses = statusResponses.filter(r => String(r.statusCheckId) === String(c.id));
      responses.filter(r => inDateRange(r.respondedAt)).forEach(r => {
        rows.push({
          CheckTitle: c.title,
          CheckQuestion: c.question,
          CheckDeadline: c.deadline,
          OrgName: r.orgName,
          Response: r.response,
          Notes: r.notes,
          RespondedAt: r.respondedAt,
        });
      });
    });
    downloadCSV(rows, `status-check-results${selectedIncidentName ? `-${selectedIncidentName}` : ''}-${todayISO()}.csv`);
  };

  const exportOffers = () => {
    const filtered = incOffers.filter(o => inDateRange(o.createdAt));
    const rows = filtered.map(o => ({
      OrgName: o.orgName,
      Type: offerLabel(o.offerType),
      Quantity: o.quantity,
      Status: o.status,
      Description: o.description,
      OfferedBy: o.offeredBy,
      CreatedAt: o.createdAt,
      Notes: o.notes,
    }));
    downloadCSV(rows, `resource-offers${selectedIncidentName ? `-${selectedIncidentName}` : ''}-${todayISO()}.csv`);
  };

  const exportFacilities = () => {
    const rows = facilities.map(f => {
      const org = organizations.find(o => String(o.id) === String(f.orgId));
      return {
        OrgName: org?.name || '',
        FacilityName: f.name,
        Type: f.type,
        Address: f.address,
        City: f.city,
        State: f.state,
        ZIP: f.zip,
        BuildingType: f.buildingType,
        SqFt: f.sqft,
        Stories: f.stories,
        OccDay: f.occDay,
        OccNight: f.occNight,
        HasGenerator: f.hasGen,
        HasHazmat: f.hasHazmat,
      };
    });
    downloadCSV(rows, `facilities_${todayISO()}.csv`);
  };

  const exportContacts = () => {
    const rows = contacts.map(c => {
      const org = organizations.find(o => String(o.id) === String(c.orgId));
      return {
        OrgName: org?.name || '',
        Name: c.name,
        Title: c.title,
        Dept: c.dept,
        Type: c.type,
        Email: c.email,
        Phone: c.phone,
        AltPhone: c.altPhone,
        CanAuthorize: c.canAuth,
      };
    });
    downloadCSV(rows, `contacts_${todayISO()}.csv`);
  };

  const exportHandlers: Record<string, () => void> = {
    organizations: exportOrganizations,
    damage: exportDamage,
    status: exportStatus,
    offers: exportOffers,
    facilities: exportFacilities,
    contacts: exportContacts,
  };

  // ── Summary Stats ─────────────────────────────────────────────

  if (initialLoad) {
    return <Center h={400}><Loader color="cyan" /></Center>;
  }

  return (
    <div>
      <Group justify="space-between" mb="lg">
        <div>
          <Title order={3}>Reports</Title>
          <Text size="sm" c="dimmed">
            {selectedIncidentId
              ? `${incidents.find(i => String(i.id) === selectedIncidentId)?.name || 'Incident'} -- Export data for analysis`
              : 'All data -- Export data for analysis'}
          </Text>
        </div>
        <Select
          placeholder="All incidents"
          data={incidentOptions}
          value={selectedIncidentId}
          onChange={setSelectedIncidentId}
          clearable
          style={{ minWidth: 220 }}
        />
      </Group>

      {/* Summary Stats */}
      <SimpleGrid cols={{ base: 2, sm: 3, md: 5 }} mb="md" spacing="sm">
        {[
          { label: 'Organizations', value: organizations.length, color: '#0891b2' },
          { label: 'Damage Reports', value: incDamage.length, color: semantic.error },
          { label: 'Resource Offers', value: incOffers.length, color: '#07963F' },
          { label: 'Status Checks', value: incChecks.length, color: primary[700] },
          { label: 'Facilities', value: facilities.length, color: '#0285C4' },
        ].map(s => (
          <Paper key={s.label} p="sm" radius="sm" withBorder ta="center" style={{ borderTop: `3px solid ${s.color}` }}>
            <Text size="xs" c="dimmed">{s.label}</Text>
            <Text fz={24} fw={700} c={s.color}>{s.value}</Text>
          </Paper>
        ))}
      </SimpleGrid>

      {/* Date Range Filters */}
      <Paper p="md" radius="sm" withBorder mb="lg">
        <Text fw={600} size="sm" mb="sm">Date Range Filter (applies to time-based exports)</Text>
        <Group gap="sm">
          <TextInput
            type="date"
            label="From"
            size="xs"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            style={{ width: 160 }}
          />
          <TextInput
            type="date"
            label="To"
            size="xs"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            style={{ width: 160 }}
          />
          {(dateFrom || dateTo) && (
            <Button variant="subtle" size="xs" color="gray" onClick={() => { setDateFrom(''); setDateTo(''); }} mt={20}>
              Clear
            </Button>
          )}
        </Group>
      </Paper>

      {/* Export Cards */}
      <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} mb="lg">
        {REPORT_TYPES.map(rt => (
          <Paper key={rt.id} p="md" radius="sm" withBorder style={{ borderLeft: `3px solid ${rt.color}` }}>
            <Group gap="xs" mb="xs">
              <div style={{ color: rt.color }}>{rt.icon}</div>
              <Text fw={600} size="sm">{rt.label}</Text>
            </Group>
            <Text size="xs" c="dimmed" mb="sm">{rt.description}</Text>
            <Button
              size="xs"
              variant="light"
              color="cyan"
              leftSection={<IconFileTypeCsv size={14} />}
              onClick={() => exportHandlers[rt.id]?.()}
              fullWidth
            >
              Generate CSV
            </Button>
          </Paper>
        ))}
      </SimpleGrid>

    </div>
  );
}
